import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  const checkAuth = useCallback(async () => {
    // Skip check if returning from Google OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('token')) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch {
      setUser(null);
      localStorage.removeItem('jwt_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    // Check if Google OAuth is configured
    api.get('/auth/google/config').then(r => {
      setGoogleEnabled(r.data.enabled || false);
    }).catch(() => {});
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem('jwt_token', token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, country_of_residence) => {
    const response = await api.post('/auth/register', { name, email, password, country_of_residence });
    const { token, user: userData } = response.data;
    localStorage.setItem('jwt_token', token);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${backendUrl}/api/auth/google/login`;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('jwt_token');
    setUser(null);
  };

  const setAuthUser = (userData) => setUser(userData);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, setAuthUser, checkAuth, googleEnabled }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
